type StockStatusTextProps = {
  stockStatus: string;
  dictionary: {
    [key: string]: string;
  };
  deliveryMin?: string;
  deliveryMax?: string;
  deliveryUnit?: string;
  stockQuantity?: number | null;
  stockLimit?: number;
  isShortened?: boolean;
  nextDeliveryDate?: Date;
  locale?: string;
};

const getLatestDate = (date1: Date, date2: Date) =>
  date1 > date2 ? date1 : date2;

const isInStock = (stockStatus: string) =>
  stockStatus === "StockItem" || stockStatus === "PendingSoldOut";

const formatDateWithLocale = (date: Date, locale = "default") =>
  date.toLocaleDateString(locale, { month: "long", year: "numeric" });

const hasDatePassed = (referenceDate: Date, comparisonDate: Date) =>
  comparisonDate.getTime() < referenceDate.getTime();

export default function getStockStatusText({
  stockStatus,
  dictionary,
  deliveryMin,
  deliveryMax,
  stockQuantity,
  stockLimit,
  isShortened,
  nextDeliveryDate,
  locale,
  deliveryUnit,
}: StockStatusTextProps): string | undefined {
  if (!dictionary) return undefined;

  const dateToday = new Date();

  if (
    !isInStock(stockStatus) &&
    nextDeliveryDate &&
    !hasDatePassed(dateToday, nextDeliveryDate)
  ) {
    if (isShortened) {
      return dictionary.inStockSoon;
    }

    const localeArgument = locale === "eu" ? "en-GB" : locale;
    const formattedDate = formatDateWithLocale(
      getLatestDate(nextDeliveryDate, dateToday),
      localeArgument,
    );

    return dictionary.onTheWayToUsBackInStock?.replace("%X%", formattedDate);
  }

  switch (stockStatus) {
    case "PendingSoldOut":
    case "StockItem":
      if (stockLimit) {
        if (
          stockQuantity &&
          stockQuantity < stockLimit &&
          dictionary.lessThanInStock
        ) {
          return dictionary.lessThanInStock.replace("%X%", `${stockQuantity}`);
        }

        if (
          // stockQuantity is supposedly passed as undefined from BE when
          // stock status is StockItem and items in stock are > 10
          ((stockQuantity && stockQuantity >= stockLimit) ||
            stockQuantity === undefined) &&
          dictionary.moreThanStockLimitInStock
        ) {
          return dictionary.moreThanStockLimitInStock.replace(
            "%X%",
            `${stockLimit}`,
          );
        }
      }
      return dictionary.inStock;
    case "Incoming":
      return dictionary.inStockSoon;
    case "DeliveryItem":
      if (
        deliveryMin &&
        deliveryMax &&
        deliveryMin !== "undefined" &&
        deliveryMax !== "undefined" &&
        deliveryMin !== "NaN" &&
        deliveryMax !== "NaN"
      ) {
        const minAndMax = [deliveryMin, deliveryMax].join("-");
        const unit =
          deliveryUnit === "week" && dictionary[deliveryUnit]
            ? dictionary[deliveryUnit]
            : `${deliveryUnit}s`;
        return `${dictionary.madeToOrder}: ${dictionary.preliminaryDeliveryTime} ${minAndMax} ${unit}`;
      }
      return dictionary.madeToOrder;
    case "OutOfStock":
      return dictionary.outOfStock;
    case "SoldOut":
      return dictionary.soldOut;
    default:
      return undefined;
  }
}
